import React, {
	type ComponentProps,
	type ComponentType,
	type ErrorInfo,
	type PropsWithChildren,
	useCallback,
} from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ErrorBoundary as CommonErrorBoundary } from '@atlassian/search-common/error-boundary';
import { SearchDialog } from '@atlassian/search-dialog/search-dialog';

import { NLPSearchErrorState } from '../ai-answer-dialog/nlp-search-error-state';
import { ReadingAidsContainer } from '../ai-answer-dialog/reading-aids-container';
import { ReadingAidsPopupContainer } from '../ai-answer-dialog/reading-aids-popup-container';
import { getModuleFetchErrorAttributes } from '../ai-answer-dialog/utils/get-module-fetch-error-attributes';
import { getModuleLoadErrorAttributes } from '../ai-answer-dialog/utils/get-module-load-error-attributes';
import { logger, type LogProps } from '../common/utils/logger';
import { AnswerCardType, DEFAULT_GAS_CHANNEL, type QueryIntent } from '../constants';
import { getAnswerCardType } from '../get-answer-card-type';
import { isHello } from '../is-hello';
import { messages } from './messages';

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	readingAids: {
		paddingTop: token('space.150'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.300'),
		font: token('font.body.large'),
		color: token('color.text.accent.gray.bolder'),
		maxWidth: '460px',
		maxHeight: '200px',
		overflowY: 'auto',
	},
	nonReadingAids: {
		paddingTop: token('space.250'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.300'),
		font: token('font.body'),
		color: token('color.text.accent.gray'),
		maxHeight: '417px',
		overflowY: 'auto',
		width: '100%',
	},
});

const MAX_STACK_FRAMES = 10;

/**
 * Trims a stack trace (either an error stack or a React component stack) to the
 * first {@link MAX_STACK_FRAMES} lines. Stack traces contain only code metadata
 * (file names, line numbers and React component display names) and never
 * user-generated content, but we cap the number of frames to keep the analytics
 * payload bounded.
 */
const getTruncatedStack = (stack?: string): string | undefined => {
	if (!stack) {
		return undefined;
	}

	return stack.split('\n').slice(0, MAX_STACK_FRAMES).join('\n');
};

/**
 * Normalises {@link Error.cause} into a safe, bounded string for diagnostics.
 * Only the error name/message (or a primitive value) is surfaced so that we do
 * not accidentally spread an arbitrary object that could carry user-generated
 * content.
 */
const getErrorCause = (cause: unknown): string | undefined => {
	if (cause == null) {
		return undefined;
	}

	if (cause instanceof Error) {
		return cause.name ? `${cause.name}: ${cause.message}` : cause.message;
	}

	if (typeof cause === 'string' || typeof cause === 'number' || typeof cause === 'boolean') {
		return String(cause);
	}

	return undefined;
};

type ErrorBoundaryProps = ComponentProps<'div'> & {
	cloudId: string;
	feature?: LogProps['feature'];
	isReadingAids?: boolean;
	wrapper?: ComponentType<PropsWithChildren<object>>;
	source?: string;
	smartAnswersRoute?: QueryIntent;
};

/**
 *	@private
 *	@deprecated Use the ErrorBoundary in common instead
 *	TODO Rewrite to use the error boundry from common
 */
export const ErrorBoundary = ({
	isReadingAids,
	wrapper,
	source,
	cloudId,
	feature = 'sain-text-answer',
	children,
	smartAnswersRoute,
}: ErrorBoundaryProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const answerCardType = getAnswerCardType(!!isReadingAids, smartAnswersRoute);

	const DefaultWrapper = isReadingAids
		? (props: { children: React.ReactNode }) => (
				<ReadingAidsPopupContainer>
					<ReadingAidsContainer>{props.children}</ReadingAidsContainer>
				</ReadingAidsPopupContainer>
			)
		: SearchDialog;
	const Wrapper = wrapper ? wrapper : DefaultWrapper;

	const logError = useCallback(
		(error: Error, errorInfo?: ErrorInfo) => {
			// Currently we are in between deprecating UFOv1 and adopting react-UFO  for platform packages, so this is a temporary way for us to collect error logs
			// https://atlassian.slack.com/archives/C01AY8132S3/p1716509785878689
			const includeAdditionalDetails = fg('kd_sain_error_boundary_addtl_details');
			const errorAttributes = {
				errorName: error.name,
				errorMessage: error.message,
				// The stack trace and component stack contain only code metadata (file
				// names, line numbers and React component display names), never
				// user-generated content, so they are safe to emit for diagnostics.
				errorStack: includeAdditionalDetails
					? getTruncatedStack(error.stack)
					: error.stack?.split('\n').at(1),
				isHello: isHello(cloudId),
				answerCardType,
				...(includeAdditionalDetails && {
					componentStack: getTruncatedStack(errorInfo?.componentStack ?? undefined),
					// `Error.cause` is only typed under the es2022 lib; read it defensively
					// so we do not depend on the package's TS lib target.
					errorCause: getErrorCause((error as { cause?: unknown }).cause),
				}),
			};

			createAnalyticsEvent({
				action: 'failed',
				actionSubject: 'aiAnswerDialog',
				source: source || 'unknown',
				eventType: 'track',
				attributes: errorAttributes,
			}).fire(DEFAULT_GAS_CHANNEL);

			if (fg('kd_sain_error_boundary_shown_event')) {
				const moduleLoadErrorAttributes =
					answerCardType === AnswerCardType.SAIN
						? fg('kd_sain_resource_load_errors') && getModuleFetchErrorAttributes(error)
						: fg('kd_knowledge_card_resource_load_errors') && getModuleLoadErrorAttributes(error);
				createAnalyticsEvent({
					action: 'shown',
					actionSubject: 'aiAnswerError',
					source: source || 'unknown',
					eventType: 'track',
					attributes: {
						...errorAttributes,
						errorState: NLPSearchErrorState.RenderError,
						...moduleLoadErrorAttributes,
					},
				}).fire(DEFAULT_GAS_CHANNEL);
			}

			logger.error({
				feature,
				element: 'container',
				operation: 'render',
				category: 'render',
				error,
				legacyError: errorAttributes,
				functionName: 'ErrorBoundary: Depricated',
				...(includeAdditionalDetails && {
					context: {
						componentStack: getTruncatedStack(errorInfo?.componentStack ?? undefined),
						errorCause: getErrorCause((error as { cause?: unknown }).cause),
					},
				}),
			});
		},
		[source, createAnalyticsEvent, cloudId, answerCardType, feature],
	);

	return (
		<CommonErrorBoundary
			fallback={
				<Wrapper>
					<Box xcss={cx(styles.root, isReadingAids ? styles.root : styles.readingAids)}>
						{formatMessage(messages.error_boundary_fallback_text)}
					</Box>
				</Wrapper>
			}
			onException={logError}
		>
			{children}
		</CommonErrorBoundary>
	);
};
